// The seamless launcher (spec 295) — the three connect strategies, FedCM-first, in order:
//   1. FedCM (browser-native, silent when a Home session exists; spec 264 / ADR-0032),
//   2. first-party popup (WebAuthn/OAuth on the Home origin; COOP-relay return),
//   3. full-page redirect (the guaranteed fallback; popup-blocked / mobile).
// The relying app composes these (try FedCM → catch → popup → on blocked → redirect), so a device-custodied
// member who can't FedCM falls through to where they CAN sign. Generic (ADR-0021): the Home origin, the
// FedCM clientId, and the relay-channel name are all parameters.
import { fedcmGet } from '@agenticprimitives/fedcm-rp';
import { randomB64url } from './oidc.js';
import { openCentralAuthPopup } from './popup.js';
const stashOf = (name, s) => ({
    name, state: s.state, authOrigin: s.authOrigin, codeVerifier: s.codeVerifier, nonce: s.nonce,
});
/** REDIRECT strategy: stash PKCE, then navigate to the Home. Returns the stash to persist BEFORE the
 *  navigation (the caller writes it to sessionStorage, then sets `window.location.href = start.url`). */
export async function connectViaRedirect(client, name) {
    const start = await client.startEnrollment((name ?? '').trim());
    return { url: start.url, stash: stashOf((name ?? '').trim(), start) };
}
/** POPUP strategy: open the Home's `/authorize` in a first-party popup; return the code IN PLACE (no
 *  navigation). On `blocked` the caller falls back to `connectViaRedirect` (ADR-0013, explicit not silent). */
export async function connectViaPopup(client, name, relayChannel, onProgress, signal) {
    const trimmed = (name ?? '').trim();
    // `openCentralAuthPopup` appends `mode=popup` itself — don't double-add it here.
    const start = await client.startEnrollment(trimmed);
    const stash = stashOf(trimmed, start);
    // Accept the code from any origin the app trusts as a Home, not just the one we opened: a NAMELESS apex
    // enroll is hopped by the Home to the member's `<label>` subdomain mid-ceremony (passkey rpId isolation /
    // per-handle home), so the code is minted + returned from the subdomain. `res.authOrigin` is where it was
    // actually minted → exchange THERE so the id_token `iss` matches (else exchange/verify fails on the hop).
    const res = await openCentralAuthPopup(start.url, start.state, start.authOrigin, relayChannel, onProgress, signal, client.isAllowedIssuerOrigin);
    if (res.status === 'success') {
        return { status: 'success', authOrigin: res.authOrigin ?? start.authOrigin, code: res.code, codeVerifier: start.codeVerifier, stash };
    }
    return res;
}
/** FEDCM strategy (spec 264 / ADR-0032) — a TWO-step flow that keeps FedCM a thin identity adapter:
 *   1. FedCM proves IDENTITY → a thin id_token (silent when the member's session is at the Home origin).
 *   2. POST that id_token to the Home's `/fedcm/grant` → the scoped delegation. A server-custodied member
 *      (Google/KMS) is signed server-side (zero prompt); a device-custodied member returns
 *      `needs_device_credential` → THROWS → the caller falls back to popup/redirect (where the device signs).
 *  THROWS on dismissal / error / `needs_device_credential` / missing delegation — caller catches + falls back. */
export async function connectViaFedcm(home, clientId, onProgress, opts) {
    const configPath = opts?.configPath ?? '/fedcm/config.json';
    const grantPath = opts?.grantPath ?? '/fedcm/grant';
    const { token: idToken } = await fedcmGet({
        providers: [{ configURL: `${home}${configPath}`, clientId, params: { nonce: randomB64url(16), intent: 'signin' } }],
        context: 'signin',
    });
    if (!idToken)
        throw new Error('FedCM returned no id_token'); // → popup fallback
    onProgress?.('Authorizing…');
    const res = await fetch(`${home}${grantPath}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ id_token: idToken, client_id: clientId }),
    });
    const out = (await res.json().catch(() => ({})));
    if (out.needs_device_credential)
        throw new Error(`needs_device_credential:${out.via ?? ''}`); // → popup (device signs)
    if (!res.ok || !out.delegation)
        throw new Error(out.error ? `fedcm_grant_failed:${out.error}` : 'FedCM grant returned no delegation');
    return { status: 'fedcm-success', authOrigin: home, idToken, delegation: out.delegation };
}
//# sourceMappingURL=launch.js.map